/**
 * Historical dashboard recipe, stream, and error contracts.
 *
 * The model may author only the closed-world recipe. Canonicalization sorts a
 * valid panel set into registry order and otherwise fails closed.
 */

export const HISTORICAL_DASHBOARD_RECIPE_SCHEMA = 'historical-dashboard-recipe/v1';
export const HISTORICAL_DASHBOARD_PAYLOAD_SCHEMA = 'historical-dashboard/v1';
export const HISTORICAL_DASHBOARD_REQUEST_SCHEMA = 'historical-dashboard-request/v1';
export const HISTORICAL_DASHBOARD_QUERY_MESSAGE_SCHEMA = 'historical-dashboard-query/v1';
export const HISTORICAL_DASHBOARD_PROGRESS_SCHEMA = 'historical-progress/v1';
export const HISTORICAL_DASHBOARD_TRACE_SCHEMA = 'historical-decision-trace/v1';
export const HISTORICAL_DASHBOARD_TERMINAL_SCHEMA = 'historical-terminal/v1';
export const HISTORICAL_DASHBOARD_ROOT_QUERY_KEY = 'historical.dashboard-composer.v1';
export const HISTORICAL_DASHBOARD_METHOD_VERSION = 'dashboard-v1';
export const HISTORICAL_DASHBOARD_SURFACE = 'techfund_historical';
export const HISTORICAL_DASHBOARD_DATASET_KEY = 'techfund_operating_history';
export const HISTORICAL_DASHBOARD_COHORT_KEY = 'greenpoint_core_seven';
export const HISTORICAL_DASHBOARD_METRIC_KEY = 'revenue';
export const HISTORICAL_DASHBOARD_BASIS = 'actual_point_in_time';
export const HISTORICAL_DASHBOARD_MAX_PAYLOAD_BYTES = 131_072;
export const RESULT_REF_RE = /^har_[0-9a-f]{64}$/;
export const SHA256_RE = /^[0-9a-f]{64}$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const SOURCE_KEY_RE = /^[a-z0-9][a-z0-9._:-]{0,159}$/;
export const CAVEAT_KEY_RE = SOURCE_KEY_RE;
export const QUALITY_SEVERITIES = Object.freeze(['info', 'warning', 'critical']);
export const METHODOLOGY_METHOD_KEYS = Object.freeze([
  'multiple', 'duration', 'cagr', 'aggregate_multiple', 'aggregate_cagr', 'fx', 'basis',
]);

export const HISTORICAL_DASHBOARD_ERROR_CODES = Object.freeze({
  invalid: 'historical_dashboard_invalid',
  tooLarge: 'historical_dashboard_too_large',
  recipeDenied: 'historical_dashboard_recipe_denied',
  terminalConflict: 'historical_dashboard_terminal_conflict',
  resultStale: 'historical_dashboard_result_stale',
  unavailable: 'historical_dashboard_unavailable',
  disabled: 'historical_agent_disabled',
});

export const PANEL_REGISTRY = Object.freeze([
  Object.freeze({
    panelKey: 'cohort_kpi_strip',
    queryKey: 'historical.cohort-revenue-kpis.v1',
    componentKey: 'PortfolioKpiStrip',
    tableViewKey: 'entry_latest_comparison',
    blockType: 'portfolio-kpi-strip',
  }),
  Object.freeze({
    panelKey: 'cohort_trajectory',
    queryKey: 'historical.cohort-revenue-trajectory.v1',
    componentKey: 'CohortTrajectory',
    tableViewKey: 'cohort_series',
    blockType: 'cohort-trajectory',
  }),
  Object.freeze({
    panelKey: 'company_small_multiples',
    queryKey: 'historical.company-revenue-shapes.v1',
    componentKey: 'CompanySmallMultiples',
    tableViewKey: 'company_series',
    blockType: 'company-small-multiples',
  }),
  Object.freeze({
    panelKey: 'data_quality',
    queryKey: 'historical.revenue-data-quality.v1',
    componentKey: 'DataQualitySummary',
    tableViewKey: 'quality_flags',
    blockType: 'data-quality-summary',
  }),
  Object.freeze({
    panelKey: 'methodology',
    queryKey: 'historical.revenue-methodology.v1',
    componentKey: 'MethodologyCard',
    tableViewKey: 'methodology_notes',
    blockType: 'methodology-card',
  }),
]);

export const PANEL_BY_KEY = Object.freeze(Object.fromEntries(
  PANEL_REGISTRY.map((panel) => [panel.panelKey, panel]),
));

export const DASHBOARD_COMPONENT_KEYS = Object.freeze(
  PANEL_REGISTRY.map((panel) => panel.componentKey),
);

export const MILESTONE_THRESHOLD_KEYS = Object.freeze([
  'revenue_10m', 'revenue_25m', 'revenue_50m', 'revenue_100m',
]);
export const MILESTONE_THRESHOLDS_USD_M = Object.freeze([10, 25, 50, 100]);
export const MULTIPLE_BUCKET_KEYS = Object.freeze([
  'below_1x', '1_to_2x', '2_to_3x', '3_to_5x', '5_to_10x', '10x_plus',
]);
export const CAGR_BUCKET_KEYS = Object.freeze([
  'below_0', '0_to_10', '10_to_25', '25_to_50', '50_to_100', '100_plus',
]);

export const PANEL_REGISTRY_V3 = Object.freeze([
  ...PANEL_REGISTRY,
  Object.freeze({
    panelKey: 'revenue_contribution_waterfall',
    queryKey: 'historical.revenue-contribution-waterfall.v1',
    componentKey: 'RevenueContributionWaterfall',
    tableViewKey: 'contribution_rows',
    blockType: 'revenue-contribution-waterfall',
  }),
  Object.freeze({
    panelKey: 'growth_acceleration_ranking',
    queryKey: 'historical.growth-acceleration-ranking.v1',
    componentKey: 'GrowthAccelerationRanking',
    tableViewKey: 'acceleration_rows',
    blockType: 'growth-acceleration-ranking',
  }),
  Object.freeze({
    panelKey: 'scale_growth_scatter',
    queryKey: 'historical.scale-growth-scatter.v1',
    componentKey: 'ScaleGrowthScatter',
    tableViewKey: 'scale_growth_rows',
    blockType: 'scale-growth-scatter',
  }),
  Object.freeze({
    panelKey: 'revenue_concentration_trend',
    queryKey: 'historical.revenue-concentration-trend.v1',
    componentKey: 'ConcentrationTrend',
    tableViewKey: 'concentration_rows',
    blockType: 'concentration-trend',
  }),
  Object.freeze({
    panelKey: 'time_to_scale_milestones',
    queryKey: 'historical.time-to-scale-milestones.v1',
    componentKey: 'MilestoneTimeline',
    tableViewKey: 'milestone_rows',
    blockType: 'milestone-timeline',
  }),
  Object.freeze({
    panelKey: 'reporting_coverage_heatmap',
    queryKey: 'historical.reporting-coverage-heatmap.v1',
    componentKey: 'ReportingCoverageHeatmap',
    tableViewKey: 'coverage_rows',
    blockType: 'reporting-coverage-heatmap',
  }),
  Object.freeze({
    panelKey: 'company_diagnostics',
    queryKey: 'historical.company-diagnostics.v1',
    componentKey: 'CompanyDiagnostics',
    tableViewKey: 'company_diagnostic_rows',
    blockType: 'company-diagnostics',
  }),
  Object.freeze({
    panelKey: 'cohort_distribution',
    queryKey: 'historical.cohort-distribution.v1',
    componentKey: 'CohortDistribution',
    tableViewKey: 'distribution_rows',
    blockType: 'cohort-distribution',
  }),
]);

export const PANEL_BY_KEY_V3 = Object.freeze(Object.fromEntries(
  PANEL_REGISTRY_V3.map((panel) => [panel.panelKey, panel]),
));

export const DASHBOARD_COMPONENT_KEYS_V3 = Object.freeze(
  PANEL_REGISTRY_V3.map((panel) => panel.componentKey),
);

export const EXPANDED_PANEL_KEYS = Object.freeze(
  PANEL_REGISTRY_V3.slice(PANEL_REGISTRY.length).map((panel) => panel.panelKey),
);

export const RECIPE_ROOT_KEYS = Object.freeze([
  'schemaVersion', 'datasetKey', 'cohortKey', 'metricKey', 'basis', 'panels',
  'supportingTable', 'trace',
]);

export const RECIPE_PANEL_KEYS = Object.freeze([
  'panelKey', 'queryKey', 'componentKey', 'tableViewKey',
]);

export const FORBIDDEN_RECIPE_KEYS = Object.freeze(new Set([
  'sql', 'html', 'code', 'css', 'style', 'styles', 'title', 'prose', 'formula',
  'value', 'values', 'date', 'result_ref', 'resultref', 'resultRef', 'displaymode',
  'displayMode', 'component', 'componentname', 'points', 'rows',
]));

export const PROGRESS_STAGES = Object.freeze({
  interpreting: 'prompt_received',
  validating_recipe: 'recipe_validated',
  running_queries: 'managed_queries_started',
  assembling_snapshot: 'managed_snapshot_ready',
  persisting: 'conversation_persisted',
});

export const CANONICAL_TRAJECTORY_MODES = Object.freeze([
  'entry_indexed_log',
  'annualized_revenue_calendar',
]);

export const DEFAULT_TRAJECTORY_MODE = 'entry_indexed_log';

function isObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function stableJson(value) {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isObject(value)) {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

export async function sha256Hex(value) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function panelRegistryPairs(registry = PANEL_REGISTRY) {
  return registry.map((panel) => ({
    panelKey: panel.panelKey,
    queryKey: panel.queryKey,
    componentKey: panel.componentKey,
    tableViewKey: panel.tableViewKey,
  }));
}

export function expandedPanelRegistryPairs() {
  return panelRegistryPairs(PANEL_REGISTRY_V3);
}

export const ROOT_QUERY_SPEC_KEYS = Object.freeze([
  'method_version',
  'dataset_sha256',
  'source_default_query_definition_sha256',
  'child_query_definition_sha256s',
  'panels',
]);

export const CHILD_QUERY_SPEC_KEYS = Object.freeze([
  'method_version',
  'dataset_sha256',
  'source_default_query_definition_sha256',
  'panel_key',
  'query_key',
  'component_key',
  'table_view_key',
]);

export const CHILD_QUERY_SPEC_KEYS_MILESTONE = Object.freeze([
  ...CHILD_QUERY_SPEC_KEYS,
  'thresholds_usd_m',
]);

export const ROOT_QUERY_SPEC_KEYS_V3 = Object.freeze([
  'method_version',
  'dataset_sha256',
  'source_default_query_definition_sha256',
  'child_query_definition_sha256s',
  'panels',
  'entity_codes',
  'periods',
  'period_policy',
  'view_policy',
  'v1_compatibility',
  'v2_compatibility',
]);

export function rootSpecRegistryIsExact(spec) {
  if (!spec || typeof spec !== 'object' || !Array.isArray(spec.panels)) return false;
  if (!exactKeys(spec, ROOT_QUERY_SPEC_KEYS)) return false;
  const expected = panelRegistryPairs();
  if (spec.panels.length !== expected.length) return false;
  return expected.every((panel, index) => {
    const row = spec.panels[index];
    return !!row
      && exactKeys(row, ['panelKey', 'queryKey', 'componentKey', 'tableViewKey'])
      && row.panelKey === panel.panelKey
      && row.queryKey === panel.queryKey
      && row.componentKey === panel.componentKey
      && row.tableViewKey === panel.tableViewKey;
  });
}

export function rootSpecRegistryIsExactV3(spec) {
  if (!spec || typeof spec !== 'object' || !Array.isArray(spec.panels)) return false;
  if (!exactKeys(spec, ROOT_QUERY_SPEC_KEYS_V3)) return false;
  const expected = expandedPanelRegistryPairs();
  if (spec.panels.length !== expected.length) return false;
  return expected.every((panel, index) => {
    const row = spec.panels[index];
    return !!row
      && exactKeys(row, ['panelKey', 'queryKey', 'componentKey', 'tableViewKey'])
      && row.panelKey === panel.panelKey
      && row.queryKey === panel.queryKey
      && row.componentKey === panel.componentKey
      && row.tableViewKey === panel.tableViewKey;
  });
}

export function exactKeys(value, allowed) {
  if (!isObject(value)) return false;
  const keys = Object.keys(value);
  return keys.length === allowed.length && keys.every((key) => allowed.includes(key));
}

export function forbiddenRecipeField(value, depth = 0) {
  if (depth > 8) return 'request_depth';
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = forbiddenRecipeField(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (!isObject(value)) return null;
  for (const [key, nested] of Object.entries(value)) {
    if (FORBIDDEN_RECIPE_KEYS.has(key) || FORBIDDEN_RECIPE_KEYS.has(key.toLowerCase())) return key;
    const found = forbiddenRecipeField(nested, depth + 1);
    if (found) return found;
  }
  return null;
}

export function recipeDenied(message) {
  const error = new Error(message);
  error.code = HISTORICAL_DASHBOARD_ERROR_CODES.recipeDenied;
  error.status = 422;
  return error;
}

export function dashboardInvalid(message) {
  const error = new Error(message);
  error.code = HISTORICAL_DASHBOARD_ERROR_CODES.invalid;
  error.status = 400;
  return error;
}

export function payloadTooLarge() {
  const error = new Error('Historical dashboard payload exceeds the 128KiB limit.');
  error.code = HISTORICAL_DASHBOARD_ERROR_CODES.tooLarge;
  error.status = 413;
  return error;
}

function assertPanelPair(panel) {
  if (!exactKeys(panel, RECIPE_PANEL_KEYS)) {
    throw recipeDenied('Dashboard panel has unknown or missing fields.');
  }
  const expected = PANEL_BY_KEY[panel.panelKey];
  if (!expected) throw recipeDenied('Dashboard panel is not in the published registry.');
  if (panel.queryKey !== expected.queryKey
    || panel.componentKey !== expected.componentKey
    || panel.tableViewKey !== expected.tableViewKey) {
    throw recipeDenied('Dashboard panel does not match the published query/component/table pairing.');
  }
}

export function canonicalizeHistoricalDashboardRecipe(value) {
  const forbidden = forbiddenRecipeField(value);
  if (forbidden) throw recipeDenied('Dashboard recipe contains a forbidden field.');
  if (!exactKeys(value, RECIPE_ROOT_KEYS)) {
    throw recipeDenied('Dashboard recipe has unknown or missing fields.');
  }
  if (value.schemaVersion !== HISTORICAL_DASHBOARD_RECIPE_SCHEMA
    || value.datasetKey !== HISTORICAL_DASHBOARD_DATASET_KEY
    || value.cohortKey !== HISTORICAL_DASHBOARD_COHORT_KEY
    || value.metricKey !== HISTORICAL_DASHBOARD_METRIC_KEY
    || value.basis !== HISTORICAL_DASHBOARD_BASIS
    || value.supportingTable !== 'follow_selected_panel'
    || value.trace !== 'available_collapsed') {
    throw recipeDenied('Dashboard recipe literals are not the published Historical contract.');
  }
  if (!Array.isArray(value.panels) || value.panels.length < 1 || value.panels.length > 5) {
    throw recipeDenied('Dashboard recipe must contain one through five unique panels.');
  }
  const seen = new Set();
  for (const panel of value.panels) {
    assertPanelPair(panel);
    if (seen.has(panel.panelKey)) throw recipeDenied('Dashboard recipe contains a duplicate panel.');
    seen.add(panel.panelKey);
  }
  const panels = PANEL_REGISTRY
    .filter((entry) => seen.has(entry.panelKey))
    .map((entry) => ({
      panelKey: entry.panelKey,
      queryKey: entry.queryKey,
      componentKey: entry.componentKey,
      tableViewKey: entry.tableViewKey,
    }));
  return {
    schemaVersion: HISTORICAL_DASHBOARD_RECIPE_SCHEMA,
    datasetKey: HISTORICAL_DASHBOARD_DATASET_KEY,
    cohortKey: HISTORICAL_DASHBOARD_COHORT_KEY,
    metricKey: HISTORICAL_DASHBOARD_METRIC_KEY,
    basis: HISTORICAL_DASHBOARD_BASIS,
    panels,
    supportingTable: 'follow_selected_panel',
    trace: 'available_collapsed',
  };
}

export function canonicalDashboardRecipe() {
  return canonicalizeHistoricalDashboardRecipe({
    schemaVersion: HISTORICAL_DASHBOARD_RECIPE_SCHEMA,
    datasetKey: HISTORICAL_DASHBOARD_DATASET_KEY,
    cohortKey: HISTORICAL_DASHBOARD_COHORT_KEY,
    metricKey: HISTORICAL_DASHBOARD_METRIC_KEY,
    basis: HISTORICAL_DASHBOARD_BASIS,
    panels: PANEL_REGISTRY.map((panel) => ({
      panelKey: panel.panelKey,
      queryKey: panel.queryKey,
      componentKey: panel.componentKey,
      tableViewKey: panel.tableViewKey,
    })),
    supportingTable: 'follow_selected_panel',
    trace: 'available_collapsed',
  });
}

export function recipeHasCanonicalPanels(recipe) {
  const keys = (recipe?.panels || []).map((panel) => panel.panelKey);
  return PANEL_REGISTRY.every((panel) => keys.includes(panel.panelKey));
}

export async function recipeSha256(recipe) {
  return sha256Hex(stableJson(canonicalizeHistoricalDashboardRecipe(recipe)));
}

export function recipesMatch(left, right) {
  try {
    return stableJson(canonicalizeHistoricalDashboardRecipe(left))
      === stableJson(canonicalizeHistoricalDashboardRecipe(right));
  } catch {
    return false;
  }
}

export function dashboardPayloadBytes(payload) {
  return new TextEncoder().encode(JSON.stringify(payload)).byteLength;
}

export function assertDashboardPayloadBudget(payload) {
  const bytes = dashboardPayloadBytes(payload);
  if (bytes > HISTORICAL_DASHBOARD_MAX_PAYLOAD_BYTES) throw payloadTooLarge();
  return bytes;
}

export function historicalProgressPart(requestId, stage) {
  const detailKey = PROGRESS_STAGES[stage];
  if (!detailKey) throw dashboardInvalid('Unknown Historical progress stage.');
  return {
    type: 'data-historical-progress',
    transient: true,
    data: {
      schemaVersion: HISTORICAL_DASHBOARD_PROGRESS_SCHEMA,
      requestId,
      stage,
      detailKey,
    },
  };
}

export function historicalTracePart({
  requestId,
  intentKey,
  queryKeys,
  definitionSha256s,
  datasetSha256,
  cohortKey,
  metricKey,
  basis,
  caveatKeys = [],
  modelId,
  provider,
  normalizedRecipe,
  definitionKeys,
  definitionVersions,
  datasetVersion,
  sourceDate,
  coverage,
  methodNames,
  validationEvents,
  snapshotLineage,
  latencyStages,
  diff,
  revisionSource,
}) {
  const data = {
    schemaVersion: HISTORICAL_DASHBOARD_TRACE_SCHEMA,
    requestId,
    intentKey,
    queryKeys,
    definitionSha256s,
    datasetSha256,
    cohortKey,
    metricKey,
    basis,
    validation: 'passed',
    revisionSource: revisionSource || 'model',
    caveatKeys,
  };
  const optional = {
    modelId, provider, normalizedRecipe, definitionKeys, definitionVersions,
    datasetVersion, sourceDate, coverage, methodNames, validationEvents,
    snapshotLineage, latencyStages, diff,
  };
  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) data[key] = value;
  }
  return {
    type: 'data-historical-trace',
    data,
  };
}

export function historicalTerminalPart({
  requestId,
  conversationId,
  revisionId,
  status,
  resultRef,
}) {
  const data = {
    schemaVersion: HISTORICAL_DASHBOARD_TERMINAL_SCHEMA,
    requestId,
    conversationId,
    revisionId,
    status,
  };
  if (status === 'persisted' && resultRef) data.resultRef = resultRef;
  return { type: 'data-historical-terminal', data };
}

export function dashboardErrorBody(error, requestId) {
  const publicCodes = new Set(Object.values(HISTORICAL_DASHBOARD_ERROR_CODES));
  const safeCode = publicCodes.has(error?.code) ? error.code : HISTORICAL_DASHBOARD_ERROR_CODES.invalid;
  const body = {
    error: publicCodes.has(error?.code)
      ? String(error?.message || 'Historical dashboard request is invalid.')
      : 'Historical dashboard request is invalid.',
    code: safeCode,
  };
  if (requestId) body.requestId = requestId;
  return body;
}

export function dashboardRequestHashInput({ email, route, requestBody }) {
  const body = requestBody && typeof requestBody === 'object' ? requestBody : {};
  return {
    lowercaseEmail: String(email || '').trim().toLowerCase(),
    route: String(route || ''),
    conversationIdOrNull: body.conversation_id || null,
    requestBody: body,
  };
}
